import { useEffect, useMemo, useRef, useState } from 'react';

import {
	CONNECTOR_AUTH_RATE_TRAIT,
	fetchSiteTraits,
	normalizeTapKeys,
	parseProductValueTrait,
} from '@atlassian/conversation-assistant-service/services/tap-service';
import { useEnabledKnowledgeSources } from '@atlassian/conversation-assistant-store/controllers/connectors-store';

import { is3pApp } from '../utils/is-3p-app';

const DEFAULT_GHOST_ICON_LIMIT = 2;
const TAP_TRAITS_FETCH_TIMEOUT_MS = 3000;

export type GhostIconConnector = {
	source: string;
	friendlyName: string;
	iconUrl: string;
	orgAuthPercent: number;
};

// Outcomes for a single TAP fetch attempt, surfaced to callers via
// `options.onFetchComplete` so they can fire an operational analytics event
// without duplicating the timing / error bookkeeping inside the hook.
//   - 'success': fetch resolved before the timeout and inside the lifetime.
//   - 'failure': fetch rejected for a non-timeout reason (network error, parse
//     error, etc.). Hook still fails open and returns `{ connectors: [] }`.
//   - 'timeout': the AbortController fired because the request took longer
//     than `TAP_TRAITS_FETCH_TIMEOUT_MS`. Distinct from 'failure' so SRE can
//     tune the timeout independently from real backend errors.
//
// 'aborted' (unmount-driven AbortController.abort()) is intentionally not
// reported: by the time it would fire, the consumer is unmounting and the
// analytics context may already be torn down.
export type GhostIconConnectorsFetchOutcome = 'success' | 'failure' | 'timeout';

export type GhostIconConnectorsFetchResult = {
	outcome: GhostIconConnectorsFetchOutcome;
	durationMs: number;
};

export type UseGhostIconConnectorsOptions = {
	limit?: number;
	// Fired exactly once per non-aborted TAP fetch attempt. Used by
	// `useGhostIconsPillState` to emit the `ghost_icons_connectors_fetch_completed`
	// operational event so we have visibility into TAP reliability separately
	// from the user-facing render funnel. Reference identity does not matter —
	// the latest callback is captured via a ref and the effect does not re-run
	// when only the callback identity changes.
	onFetchComplete?: (result: GhostIconConnectorsFetchResult) => void;
};

/**
 * Returns the top-N (default 2) unconnected 3P connectors ranked by org auth %.
 *
 * - When `enabled` is false (e.g. the consumer has determined upstream that the
 *   user is not in the ghost-icons experiment), this hook returns
 *   `{ connectors: [], loaded: true }` immediately and skips TAP fetches. This
 *   lets callers gate the fetch on a single experiment check made upstream
 *   without forcing this hook to consult the experiment itself (which would
 *   either duplicate the call or dilute exposure).
 * - When `siteId` is missing, this hook returns `{ connectors: [], loaded: true }`
 *   immediately and skips TAP fetches.
 * - When the TAP request fails, this hook fails open and returns
 *   `{ connectors: [], loaded: true }`.
 * - Connector store hydration is handled upstream by `FilteredAppsTag` via
 *   `useEnsureConnectorsLoaded`, so this hook only reads from the store.
 */
export const useGhostIconConnectors = (
	siteId: string,
	enabled: boolean,
	options?: UseGhostIconConnectorsOptions,
): { connectors: GhostIconConnector[]; loaded: boolean } => {
	const limit = options?.limit ?? DEFAULT_GHOST_ICON_LIMIT;
	const onFetchComplete = options?.onFetchComplete;

	const [allKnowledgeSources] = useEnabledKnowledgeSources();
	const [tapStats, setTapStats] = useState<Record<string, number> | null>(null);

	// Identifies the (enabled, siteId) combination that decides whether to
	// fetch and what to fetch. `null` means "no fetch needed" (loaded
	// immediately); a string means "must fetch for this key". Using a derived
	// key instead of a boolean `loaded` flag avoids a one-render lag where
	// `loaded` would still be `true` from a previous "no fetch needed" state
	// after `enabled` flips on — that transient surfaces to the parent as
	// `loaded=true && connectors=[]`, which the not-rendered classifier then
	// (incorrectly) reports as `no_eligible_connectors` before the fetch even
	// starts.
	const fetchKey = enabled && siteId ? siteId : null;

	// Which key we have actually completed a fetch for. When `fetchKey`
	// changes, `loaded` automatically reports `false` until the new fetch
	// settles and pushes the new key in here.
	const [completedFetchKey, setCompletedFetchKey] = useState<string | null>(null);
	const loaded = fetchKey === null || completedFetchKey === fetchKey;

	// Capture the latest callback in a ref so the fetch effect does not have to
	// re-run when only the callback identity changes — re-running would cancel
	// any in-flight TAP request and start a new one, which is the wrong
	// trade-off for an instrumentation hook.
	const onFetchCompleteRef = useRef(onFetchComplete);
	useEffect(() => {
		onFetchCompleteRef.current = onFetchComplete;
	}, [onFetchComplete]);

	useEffect(() => {
		if (fetchKey === null) {
			setTapStats(null);
			// Also clear the completed key so a later `null → siteId` flip with
			// the SAME siteId does not read `loaded=true` from the stale key
			// before the new fetch runs. Without this, the `true → false → true`
			// path re-surfaces the `{loaded:true, connectors:[]}` transient that
			// the fetch-key refactor fixed for the initial `false → true` flip
			// (tapStats is null here, so connectors would be [] while loaded
			// reported true).
			setCompletedFetchKey(null);
			return;
		}

		let cancelled = false;
		// Distinguishes "we aborted because the user's request took >3s" from
		// "we aborted because the consumer unmounted mid-fetch". Both surface as
		// AbortError in the catch path; only the timer path should be reported
		// as 'timeout' to callers.
		let timedOut = false;
		let outcome: GhostIconConnectorsFetchOutcome = 'failure';
		const startedAt = performance.now();
		const controller = new AbortController();
		const timeoutId = setTimeout(() => {
			timedOut = true;
			controller.abort();
		}, TAP_TRAITS_FETCH_TIMEOUT_MS);

		fetchSiteTraits(fetchKey, controller.signal)
			.then((traits) => {
				if (cancelled) {
					return;
				}
				const parsed = parseProductValueTrait(traits, CONNECTOR_AUTH_RATE_TRAIT);
				setTapStats(normalizeTapKeys(parsed));
				outcome = 'success';
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				// Fail-open path: return no connectors but mark loaded.
				setTapStats(null);
				outcome = timedOut ? 'timeout' : 'failure';
			})
			.finally(() => {
				clearTimeout(timeoutId);
				if (cancelled) {
					return;
				}
				setCompletedFetchKey(fetchKey);
				// Report after the `loaded` flip so any caller that re-renders
				// on `loaded` has the latest connector data available when the
				// event fires (e.g. for an `eligibleConnectorCount` attribute
				// computed from the rendered list).
				onFetchCompleteRef.current?.({
					outcome,
					durationMs: performance.now() - startedAt,
				});
			});

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
			controller.abort();
		};
	}, [fetchKey]);

	const connectors = useMemo(() => {
		if (!enabled || !tapStats || allKnowledgeSources.length === 0) {
			return [];
		}

		return allKnowledgeSources
			.filter((connector) => !connector.isUserOAuthed && is3pApp(connector.friendlyName))
			.map((connector) => ({
				source: connector.source,
				friendlyName: connector.friendlyName,
				iconUrl: connector.iconUrl,
				orgAuthPercent: tapStats[connector.source] ?? 0,
			}))
			.sort((a, b) => b.orgAuthPercent - a.orgAuthPercent)
			.slice(0, limit);
	}, [allKnowledgeSources, tapStats, limit, enabled]);

	return {
		connectors,
		loaded,
	};
};
